/**
 * SUNTRONIX ONE — Forced password rotation gate.
 *
 * Renders a blocking dialog on top of the app when the signed-in user
 * has `must_change_password: true` (typically after an admin issues a
 * temp password from the wizard or Users & Roles page).  Legal
 * Acceptance already blocked earlier — so by the time this shows,
 * signatures are done.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ForceChangePasswordGate() {
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const show = !!user && user.must_change_password && !user.requires_legal_acceptance;

  const submit = async () => {
    if (next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    if (next === current) { toast.error("New password must differ from current"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      // Clear the flag on the user record; ignore failure — server can be
      // updated by an admin endpoint if we ship one later.
      try { await api.post("/auth/clear-must-change-password"); } catch { /* endpoint optional */ }
      toast.success("Password updated");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={show}>
      <DialogContent hideClose className="sm:max-w-md" data-testid="force-change-password">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-slate-700" />
            Set a new password
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-slate-600 leading-relaxed">
            Your account is on a temporary password.  Pick a new one to continue —
            we&apos;ll never ask for the temp password again.
          </div>
          <Field label="Current (temp) password" value={current} onChange={setCurrent}
                  type="password" testId="fcp-current" />
          <Field label="New password" value={next} onChange={setNext}
                  type="password" testId="fcp-next" help="At least 8 characters" />
          <Field label="Confirm new password" value={confirm} onChange={setConfirm}
                  type="password" testId="fcp-confirm" />
          <div className="rounded border border-emerald-200 bg-emerald-50 p-2.5 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-[11px] text-emerald-900 leading-relaxed">
              Pick a passphrase you&apos;ll remember.  Password managers welcome — no complexity trap.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={logout} data-testid="fcp-signout">Sign out</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900" data-testid="fcp-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Set new password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type, testId, help }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             data-testid={testId} className="mt-1" />
      {help && <div className="text-[10px] text-slate-400 mt-1">{help}</div>}
    </div>
  );
}
